/**
 * Modal creates a popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Modal id="UniqueId" isOpen={isOpen} onClose={handleClose} onManageDisableScrolling={fn}>
 *   <Content />
 * </Modal>
 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, IconClose } from '../../components';
import { t } from '../../util/messages';
import css from './Modal.module.css';

const KEY_CODE_ESCAPE = 27;

class Portal extends Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

class ModalComponent extends Component {
  constructor(props) {
    super(props);
    this.handleBodyKeyUp = this.handleBodyKeyUp.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.refDiv = React.createRef();
    this.state = { portalRoot: null };
  }

  componentDidMount() {
    const { id, isOpen, onManageDisableScrolling } = this.props;
    onManageDisableScrolling(id, isOpen);
    document.body.addEventListener('keyup', this.handleBodyKeyUp);

    // Fix viewport height for mobile Safari scroll resize behaviour
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    window.addEventListener('resize', this.handleResize);

    this.setState({ portalRoot: document.getElementById('portal-root') });
  }

  componentDidUpdate(prevProps) {
    const { id, isOpen, onManageDisableScrolling } = this.props;
    if (prevProps.isOpen !== isOpen) {
      onManageDisableScrolling(id, isOpen);
      // Move focus inside modal when it opens
      if (this.props.usePortal && isOpen) {
        this.refDiv.current?.focus();
      }
    }
  }

  componentWillUnmount() {
    const { id, onManageDisableScrolling } = this.props;
    document.body.removeEventListener('keyup', this.handleBodyKeyUp);
    window.removeEventListener('resize', this.handleResize);
    onManageDisableScrolling(id, false);
  }

  handleBodyKeyUp(event) {
    if (event.keyCode === KEY_CODE_ESCAPE && this.props.isOpen) {
      this.handleClose(event);
    }
  }

  handleClose(event) {
    const { id, onClose, onManageDisableScrolling } = this.props;
    onManageDisableScrolling(id, false);
    onClose(event);
  }

  handleResize() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  render() {
    const {
      children = null,
      className = null,
      scrollLayerClassName,
      closeButtonMessage = null,
      containerClassName,
      contentClassName,
      lightCloseButton = false,
      isClosedClassName,
      isOpen = false,
      usePortal = false,
    } = this.props;

    const closeModalMessage = t('Modal.closeModal');
    const closeButtonClasses = classNames(css.close, {
      [css.closeLight]: lightCloseButton,
    });

    const closeBtn = isOpen ? (
      <Button
        onClick={this.handleClose}
        rootClassName={closeButtonClasses}
        title={closeModalMessage}
        aria-label={closeModalMessage}
      >
        <span className={css.closeText}>
          {closeButtonMessage || t('Modal.close')}
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : null;

    const isOpenClass = usePortal ? css.isOpenInPortal : css.isOpenInPlace;
    const modalClass = isOpen ? isOpenClass : (isClosedClassName || css.isClosed);
    const classes = classNames(modalClass, className);
    const scrollLayerClasses = scrollLayerClassName || css.scrollLayer;
    const containerClasses = containerClassName || css.container;
    const { portalRoot } = this.state;

    const inner = (
      <div className={classes}>
        <div className={scrollLayerClasses}>
          <div
            className={classNames(containerClasses, usePortal ? css.focusedDiv : null)}
            ref={usePortal ? this.refDiv : null}
            tabIndex={usePortal ? -1 : undefined}
            role={usePortal ? 'dialog' : undefined}
            aria-modal={usePortal ? 'true' : undefined}
          >
            {closeBtn}
            <div className={contentClassName || css.content}>
              {children}
            </div>
          </div>
        </div>
      </div>
    );

    if (!usePortal) return inner;
    if (portalRoot) return <Portal portalRoot={portalRoot}>{inner}</Portal>;
    return null;
  }
}

ModalComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  scrollLayerClassName: PropTypes.string,
  closeButtonMessage: PropTypes.node,
  containerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  lightCloseButton: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isClosedClassName: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  usePortal: PropTypes.bool,
  onManageDisableScrolling: PropTypes.func.isRequired,
};

const Modal = props => <ModalComponent {...props} />;

Modal.displayName = 'Modal';

export default Modal;
